import React, { useCallback, useMemo, useState } from 'react';

import { useWindowResize } from '../../../../../hooks/useWindowResize';
import { CalculatorField } from '../../../../admin/calculator/store/types';
import { ReadOnlyCalculatorField } from '../../../../datasets/fields/ReadOnlyCalculatorField';
import { Button } from '../../../../shared/button/Button';
import { IconButton } from '../../../../shared/button/IconButton';
import { SpaceBetweenButtonWrapper } from '../../../../shared/button/SpaceBetweenButtonWrapper';
import { Calculator as CalculatorIcon } from '../../../../shared/icons';
import { ModalHeader } from '../../../../shared/modal/ModalHeader';
import { PositionModal } from '../../../../shared/modal/PositionModal';
import styles from '../../Analytics.module.scss';

interface CalculatorRiskValueProps {
    value: CalculatorField[];
    showGracePeriod: boolean;
    showOutput: boolean;
    label: string;
}

interface CalculatorRiskValueModalProps extends CalculatorRiskValueProps {
    closeModal: () => void;
    isOpen: boolean;
}

const INITIAL_Y_OFFSET = 220;
const PAGE_MARGIN_PADDING = 80;
const TILE_PADDING = 20;

const { lightGrey } = styles;

export const CalculatorRiskValueModal: React.FC<CalculatorRiskValueModalProps> = ({
    value,
    showGracePeriod,
    showOutput,
    closeModal,
    label,
    isOpen
}) => {
    const [screenWidth] = useWindowResize();
    const modalWidth = useMemo(() => ((screenWidth - PAGE_MARGIN_PADDING) / 2) - TILE_PADDING, [screenWidth]);
    return (
        <PositionModal
            isOpen={isOpen}
            closeModal={closeModal}
            position={{ x: TILE_PADDING, y: INITIAL_Y_OFFSET }}
            testId='document-analytics-calculator-field'
            width={`${modalWidth}px`}
            height='fit content'
        >
            <ModalHeader icon={CalculatorIcon} label={label} />
            <div className={styles.calculatorRiskModalWrapper}>
                {value.map(({ calculation, calculationCondition, output, gracePeriod }, calculationIndex) => (
                    <ReadOnlyCalculatorField
                        key={calculationIndex}
                        value={value}
                        calculation={calculation}
                        output={output}
                        gracePeriod={gracePeriod}
                        calculationCondition={calculationCondition}
                        calculationIndex={calculationIndex}
                        showOutputField={showOutput}
                        showGracePeriod={showGracePeriod}
                        borderColor={lightGrey}
                    />
                ))}
            </div>
            <SpaceBetweenButtonWrapper leftButtons={[]} rightButtons={[<Button onClick={closeModal} label='Close' key='close' />]} />
        </PositionModal>
    );
};

export const CalculatorRiskValue: React.FC<CalculatorRiskValueProps> = ({ label, showGracePeriod, showOutput, value }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const openModal = useCallback(() => setIsOpen(true), []);
    const closeModal = useCallback(() => setIsOpen(false), []);
    return (
        <div>
            <IconButton icon={CalculatorIcon} onClick={openModal} />
            <CalculatorRiskValueModal
                isOpen={isOpen}
                closeModal={closeModal}
                label={label}
                showGracePeriod={showGracePeriod}
                showOutput={showOutput}
                value={value}
            />
        </div>
    );
};
